import { useEffect, useRef, useState } from 'react';
import { Copy, QrCode } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { SETTINGS_PAGE } from '../../settings/shared/settings-shared';
import { Button, Card, Typography } from '../ui';
import { useProfileLinkEntry } from './use-profile-link-entry';
import styles from './ProfileLinks.module.css';

const COPIED_MS = 2000;

/**
 * The profile link at the TOP of the Share & earn credits screen (design mock entry 2): it is the owner's
 * referral code too, so it earns the same credits as the invite link below it. Copy, QR code (the Share
 * panel) and Manage (the owner screens). Rendered by both share layouts (`ShareAppModal`,
 * `ShareAppModalMobile`); hidden outside `symphonyProfileLinks`.
 */
export function ProfileLinkShareSection({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { logModalActionBi } = useGenericBi();
  const entry = useProfileLinkEntry();
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => (timer.current ? clearTimeout(timer.current) : undefined), []);
  if (!entry.enabled) return null;
  const action = (name: string) => logModalActionBi({ ...SETTINGS_PAGE, modal_name: 'share_app', action: name });

  const copy = async () => {
    if (!entry.url) return;
    action('copy_profile_link');
    try {
      await navigator.clipboard.writeText(entry.url);
    } catch {
      return;
    }
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), COPIED_MS);
  };

  return (
    <section className={[styles.shareSection, className].filter(Boolean).join(' ')} aria-labelledby="profile-link-share-section">
      <Typography variant="tiny-semi-bold" color="secondary" component="h3" id="profile-link-share-section" className={styles.sectionLabel}>
        {t('profileLinks.shareSection.title')}
      </Typography>
      <Card variant="stone" size="sm" className={styles.shareSectionCard}>
        <Typography variant="h4" component="span" dir="ltr" className={styles.ellipsis}>
          {entry.address ?? t('profileLinks.settings.setup')}
        </Typography>
        <Typography variant="p3" color="secondary" component="span">
          {t('profileLinks.shareSection.hint')}
        </Typography>
        <div className={styles.actions}>
          {entry.url ? (
            <>
              <Button size="small" startIcon={<Copy size={16} aria-hidden />} onClick={() => void copy()}>
                {copied ? t('common.copied') : t('common.copy')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<QrCode size={16} aria-hidden />}
                onClick={() => {
                  action('profile_link_qr');
                  entry.open({ share: true });
                }}
              >
                {t('profileLinks.shareSection.qr')}
              </Button>
            </>
          ) : null}
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              action('profile_link_manage');
              entry.open();
            }}
          >
            {t('common.manage')}
          </Button>
        </div>
      </Card>
    </section>
  );
}
